import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { replaceVariables } from '@/lib/emailVariables';
import DOMPurify from 'dompurify';

interface EmailComposePreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject: string;
  bodyHtml: string;
  bodyText: string;
  variables: Record<string, string>;
  recipientEmail: string;
  onSend: () => void;
}

export default function EmailComposePreview({
  open,
  onOpenChange,
  subject,
  bodyHtml,
  bodyText,
  variables,
  recipientEmail,
  onSend,
}: EmailComposePreviewProps) {
  const previewSubject = replaceVariables(subject, variables);
  const previewHtml = replaceVariables(bodyHtml, variables);
  const previewText = replaceVariables(bodyText, variables);

  // Sanitize HTML to prevent XSS
  const sanitizedHtml = DOMPurify.sanitize(previewHtml, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'img', 'div', 'span', 'table', 'thead', 'tbody', 'tr', 'td', 'th'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'style']
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Email Preview</DialogTitle>
          <DialogDescription>
            Preview how your email will appear to the recipient
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground">To:</p>
            <p className="text-sm">{recipientEmail}</p>
          </div>

          <div>
            <p className="text-sm font-medium text-muted-foreground">Subject:</p>
            <p className="text-sm font-semibold">{previewSubject}</p>
          </div>

          <Tabs defaultValue="html" className="w-full">
            <TabsList>
              <TabsTrigger value="html">HTML Preview</TabsTrigger>
              <TabsTrigger value="text">Plain Text</TabsTrigger>
            </TabsList>
            <TabsContent value="html" className="mt-4">
              <div
                className="prose prose-sm max-w-none p-4 border rounded-md bg-background"
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              />
            </TabsContent>
            <TabsContent value="text" className="mt-4">
              <div className="whitespace-pre-wrap p-4 border rounded-md bg-background text-sm font-mono">
                {previewText}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Back to Edit
          </Button>
          <Button onClick={onSend}>
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
